import { AddImageIcon, CircleCloseIcon } from 'assets/icons';
import { CustomButton, CustomModal, CustomTextArea } from 'components';
import { FILE_TYPE } from 'constants/common';
import { FC, useEffect, useRef, useState } from 'react';
import { AttachmentFile, VoidFn } from 'types/baseTypes';
import { useCreateNoteMutation, useEditNoteMutation, useUploadJobImageFileMutation } from '../api';
import MultipleFileUpload from 'components/multiple-file-upload/MultipleFileUpload';
import { ButtonType } from 'components/custom-button/CustomButton';
import { logAnalyticEvent } from 'utils/analytics';
import { JobsCleverTapEvents } from '../events';

export interface JobNote {
  noteId: number;
  subTitle: string;
  attachments: AttachmentFile[];
}

interface CreateEditNoteModalProps {
  defaultNote?: string;
  onClose: VoidFn;
  isEdit: boolean;
  currentNote: JobNote;
  teamId: number;
  jobId: string;
  getJobTimeline: VoidFn;
  setNotesInput?: (note: string) => void;
}

const CreateEditNoteModal: FC<CreateEditNoteModalProps> = ({
  defaultNote = '',
  onClose,
  isEdit,
  currentNote,
  teamId,
  jobId,
  getJobTimeline,
  setNotesInput
}) => {
  const [comment, setComment] = useState(defaultNote);
  const [noteAttachments, setNoteAttachments] = useState<AttachmentFile[]>([]);
  const [uploadFileMutation, { isLoading: isUploadingImage, isSuccess: isUploadSuccess }] =
    useUploadJobImageFileMutation();
  const currentImageUploadCall = useRef(null);
  const [createNoteMutation, createNoteState] = useCreateNoteMutation();
  const { isLoading: isCreateNoteLoading, isSuccess: isCreateNoteSuccess } = createNoteState;
  const [editNoteMutation, editNoteState] = useEditNoteMutation();
  const { isLoading: isEditNoteLoading, isSuccess: isEditNoteSuccess } = editNoteState;

  useEffect(() => {
    if (!isEdit) logAnalyticEvent(JobsCleverTapEvents.webCreateNotePage);
  }, []);

  useEffect(() => {
    if (isEdit && currentNote) {
      setComment(currentNote.subTitle);
      setNoteAttachments(currentNote.attachments);
    }
  }, [currentNote, isEdit]);

  useEffect(() => {
    if ((!isCreateNoteLoading && isCreateNoteSuccess) || (!isEditNoteLoading && isEditNoteSuccess)) {
      getJobTimeline();
      setNotesInput?.('');
      onClose();
    }
  }, [isCreateNoteLoading, isCreateNoteSuccess, isEditNoteLoading, isEditNoteSuccess]);

  const getImageURL = async (file: File) => {
    try {
      const imageUrlFromS3Call = uploadFileMutation({
        file: file,
        teamId: teamId
      });
      currentImageUploadCall.current = imageUrlFromS3Call;
      const imageUrlFromS3 = imageUrlFromS3Call.unwrap();
      return imageUrlFromS3;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const formatAttachments = () => {
    return noteAttachments.map(({ tempId, ...rest }) => rest);
  };

  const onSaveClick = () => {
    if (isEdit) {
      editNoteMutation({
        comment: comment.trim(),
        attachments: formatAttachments(),
        noteId: currentNote.noteId,
        teamId,
        jobId
      });
    } else {
      createNoteMutation({ comment: comment.trim(), attachments: formatAttachments(), jobId, teamId });
    }
  };

  const onFileRemove = (id: number) => {
    const updatedAttachments = noteAttachments.filter(attachment => attachment.id !== id && attachment.tempId !== id);
    setNoteAttachments(updatedAttachments);
  };

  const isSaveDisabled = () => {
    if (isEdit) {
      if (comment.trim() !== currentNote.subTitle.trim()) return false;
      if (noteAttachments.length !== currentNote.attachments.length) return false;
      for (let i = 0; i < noteAttachments.length; i++) {
        if (noteAttachments[i].url !== currentNote.attachments[i].url) return false;
      }
    } else if (comment || noteAttachments.length > 0) return false;
    return true;
  };

  return (
    <CustomModal open={true} width="md:w-[610px]" height="md:min-h-[440px] md:max-h-[668px]">
      <div className="relative flex grow flex-col">
        <CircleCloseIcon onClick={onClose} className="absolute right-2 top-2 shrink-0" />

        <div className="flex items-center justify-between  px-10 pt-9">
          <div className="text-xl font-bold text-headingGray">{isEdit ? 'Edit note' : 'Create note'}</div>
        </div>
        <div className="mt-4 w-full border-b border-borderGray" />
        <div className="customNormalScroll max-h-[510px] overflow-auto px-10 pt-4 pb-2.5">
          <div className="mb-2 text-sm font-semibold text-primaryText">Note</div>
          <CustomTextArea
            id={isEdit ? 'edit-note-text-input' : 'create-note-text-input'}
            name="note-input"
            placeholder="Enter note here"
            className="h-[85px] py-3 px-5 focus:outline-none"
            value={comment}
            onChange={setComment}
          />
          <div className="mt-4 mb-2 text-sbase font-semibold text-primaryText">Attachments</div>
          <MultipleFileUpload
            id={isEdit ? 'create-note-add-attachment-card' : 'edit-note-add-attachment-card'}
            setFiles={setNoteAttachments}
            isUploadingImage={isUploadingImage}
            isUploadSuccess={isUploadSuccess}
            files={noteAttachments}
            onFileRemove={onFileRemove}
            className="mb-5"
            height="h-[100px]"
            onUpload={getImageURL}
            FileUploadIcon={AddImageIcon}
            supportedFileTypes={{
              [FILE_TYPE.JPG]: [],
              [FILE_TYPE.JPEG]: [],
              [FILE_TYPE.PNG]: [],
              [FILE_TYPE.WEBP]: []
            }}
            allowImageCompression={true}
          />
        </div>
        <div className="flex items-center justify-end border-t border-borderGray py-4 pr-10">
          <CustomButton
            id="edit-job-schedule-cancel-button"
            type={ButtonType.SECONDARY}
            onClick={onClose}
            className="mr-5 w-20">
            Cancel
          </CustomButton>
          <CustomButton
            id="edit-job-schedule-save-button"
            type={ButtonType.PRIMARY}
            onClick={onSaveClick}
            disabled={isSaveDisabled()}
            className="w-20"
            isLoading={isCreateNoteLoading || isEditNoteLoading}>
            {isEdit ? 'Save' : 'Add'}
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default CreateEditNoteModal;
